/**
 * „Kunde anrufen" — erst der Weg, dann das Gespräch.
 *
 * Ein Kunde ist selten eine Nummer: Zentrale, Durchwahlen, die Nummer, die nicht
 * über die Anmeldung läuft, dazu Ansprechpartner und Mitarbeiter mit eigenen
 * Anschlüssen. Wer nur die Hauptnummer angeboten bekommt, landet in der
 * Warteschleife, obwohl die Durchwahl gepflegt ist.
 *
 * Deshalb zeigt der Knopf zuerst alle bekannten Wege — mit ihren gepflegten
 * Bezeichnungen, unverändert aus den Stammdaten. Danach läuft es wie überall:
 * wählen, und der Dialog fragt nach dem Ergebnis.
 */
import { useEffect, useMemo, useState } from 'react';
import {
  Badge,
  Button,
  Group,
  Loader,
  Modal,
  Stack,
  Text,
  TextInput,
  ThemeIcon,
  UnstyledButton,
} from '@mantine/core';
import { useQuery } from '@tanstack/react-query';
import {
  IconBuilding,
  IconPhone,
  IconSearch,
  IconUser,
  IconUsers,
} from '@tabler/icons-react';
import { api } from '../../lib/api';
import type { CallHistoryParams, CallOriginType, CallTarget } from '../../lib/api';
import { CallOutcomeModal } from './CallOutcomeModal';
import type { CallTargetTicket } from './CallButton';
import { dialNumber, forgetPendingCall, pendingCallKey, takePendingCall } from './pendingCall';

export interface CustomerCallButtonProps extends CallHistoryParams {
  /** Anzeigename für die Überschrift der Auswahl. */
  customerName?: string;
  ticket?: CallTargetTicket | null;
  originType?: CallOriginType;
  originId?: string | null;
  label?: string;
  onLogged?: () => void;
}

const KIND_META: Record<CallTarget['kind'], { icon: typeof IconUser; label: string; color: string }> =
  {
    company: { icon: IconBuilding, label: 'Firma', color: 'blue' },
    contact: { icon: IconUser, label: 'Ansprechpartner', color: 'teal' },
    employee: { icon: IconUsers, label: 'Mitarbeiter', color: 'grape' },
  };

export function CustomerCallButton({
  customerName,
  ticket,
  originType = 'customer',
  originId,
  label = 'Kunde anrufen',
  onLogged,
  ...params
}: CustomerCallButtonProps) {
  const [pickerOpen, setPickerOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [active, setActive] = useState<
    | {
        startedAt: Date;
        phone: string;
        contactName: string;
        contactId: string | null;
      }
    | null
  >(null);

  /**
   * Der Schlüssel enthält bewusst NICHT die gewählte Nummer: Nach einem Neuladen
   * weiß dieser Knopf nicht, welche Nummer angetippt wurde — er muss den
   * angefangenen Anruf trotzdem wiederfinden. Nummer und Name stehen im
   * vermerkten Datensatz.
   */
  const pendingKey = pendingCallKey(['picker', originType, originId, ticket?.id]);

  useEffect(() => {
    const pending = takePendingCall(pendingKey);
    if (!pending) return;
    setActive({
      startedAt: new Date(pending.startedAt),
      phone: pending.phone,
      contactName: pending.contactName ?? pending.phone,
      contactId: pending.contactId ?? null,
    });
  }, [pendingKey]);

  const { data, isLoading } = useQuery({
    queryKey: ['call-targets', params],
    queryFn: () => api.getCallTargets(params),
    enabled: pickerOpen,
    staleTime: 5 * 60_000,
  });

  const targets = useMemo(() => {
    const all = data?.targets ?? [];
    const q = search.trim().toLowerCase();
    if (!q) return all;
    return all.filter(
      (t) =>
        t.name.toLowerCase().includes(q) ||
        (t.role ?? '').toLowerCase().includes(q) ||
        t.numbers.some(
          (n) => n.number.toLowerCase().includes(q) || n.label.toLowerCase().includes(q),
        ),
    );
  }, [data, search]);

  const startCall = (target: CallTarget, number: string, numberLabel: string) => {
    const contactName =
      target.kind === 'company' ? target.name : `${target.name} (${numberLabel})`;
    setPickerOpen(false);
    setActive({
      startedAt: dialNumber({
        key: pendingKey,
        phone: number,
        contactName,
        customerId: params.customerId ?? null,
        contactId: target.kind === 'contact' ? target.id : null,
        ticket: ticket ?? null,
        originType,
        originId: originId ?? null,
      }),
      phone: number,
      contactName,
      contactId: target.kind === 'contact' ? target.id : null,
    });
  };

  return (
    <>
      <Button
        size="xs"
        variant="light"
        color="teal"
        leftSection={<IconPhone size={14} />}
        onClick={() => setPickerOpen(true)}
        data-testid="customer-call-button"
      >
        {label}
      </Button>

      <Modal
        opened={pickerOpen}
        onClose={() => setPickerOpen(false)}
        title={customerName ? `${customerName} anrufen` : 'Kunde anrufen'}
        size="md"
        centered
        zIndex={2300}
      >
        <Stack gap="sm">
          <TextInput
            size="xs"
            placeholder="Name, Durchwahl oder Nummer suchen…"
            leftSection={<IconSearch size={14} />}
            value={search}
            onChange={(e) => setSearch(e.currentTarget.value)}
          />

          {isLoading && <Loader size="sm" mx="auto" my="md" />}

          {!isLoading && targets.length === 0 && (
            <Text size="sm" c="dimmed" py="md">
              Zu diesem Kunden ist keine Rufnummer gepflegt. Nummern kommen aus den
              Stammdaten — Ansprechpartner, Mitarbeiter und die Zentrale.
            </Text>
          )}

          {targets.map((target) => {
            const meta = KIND_META[target.kind];
            const Icon = meta.icon;
            return (
              <Stack key={`${target.kind}-${target.id}`} gap={4}>
                <Group gap={6} wrap="nowrap">
                  <ThemeIcon size={20} radius="xl" variant="light" color={meta.color}>
                    <Icon size={12} />
                  </ThemeIcon>
                  <Text size="sm" fw={600} truncate>
                    {target.name}
                  </Text>
                  {target.role && (
                    <Text size="xs" c="dimmed" truncate>
                      {target.role}
                    </Text>
                  )}
                </Group>
                <Group gap={6} pl={26}>
                  {target.numbers.map((n) => (
                    <UnstyledButton
                      key={`${n.label}-${n.number}`}
                      onClick={() => startCall(target, n.number, n.label)}
                      data-testid="call-target-number"
                    >
                      <Badge
                        size="lg"
                        variant="light"
                        color="teal"
                        leftSection={<IconPhone size={11} />}
                        style={{ cursor: 'pointer', textTransform: 'none' }}
                      >
                        {/* Die Bezeichnung kommt unverändert aus den Stammdaten —
                            was dort „Geheimnummer" heißt, heißt hier auch so. */}
                        {n.label}: {n.number}
                      </Badge>
                    </UnstyledButton>
                  ))}
                </Group>
              </Stack>
            );
          })}
        </Stack>
      </Modal>

      <CallOutcomeModal
        opened={active !== null}
        onClose={() => {
          forgetPendingCall();
          setActive(null);
        }}
        startedAt={active?.startedAt ?? null}
        phone={active?.phone ?? ''}
        contactName={active?.contactName ?? null}
        customerId={params.customerId ?? null}
        weclappCustomerId={params.weclappCustomerId}
        cwCompanyId={params.cwCompanyId}
        contactId={active?.contactId ?? null}
        ticket={ticket ?? null}
        originType={originType}
        originId={originId ?? null}
        {...(onLogged ? { onLogged } : {})}
      />
    </>
  );
}
